import type { IUpdateAndRemoveProps } from '@src/slide/domain/ui-model/element-modeles/Interface/base/base-model.interface';
import { copy } from '@src/slide/infrastructure/module/independent/object-operation';
import get from 'lodash/get';
import has from 'lodash/has';
import set from 'lodash/set';

export default class UpdateAndRemoveLogic {
    /**
     * 把props转为update和remove，remove是删除key （该函数相关代码迁移自style-handler.js)
     * @param uiProps  ui传过来的参数,比如：{ style: { fillBackground: val } }
     * @param modelProps  model当前对应的props
     * @returns {{update: *, remove: *}}
     */
    public static convertToUpdateAndRemove = (uiProps, modelProps): IUpdateAndRemoveProps => {
        let removeProps: any = null;
        let updateProps = uiProps;
        if (has(uiProps, 'style.fillBackground')) {
            const removeBackground = {};
            removeProps = { style: { fillBackground: removeBackground }, useBgFill: null };
            updateProps = copy(uiProps, true);

            const { fillBackground } = updateProps.style;
            UpdateAndRemoveLogic.checkSolidFill(fillBackground, removeBackground);
            UpdateAndRemoveLogic.checkGradFill(fillBackground, removeBackground, modelProps);
            UpdateAndRemoveLogic.checkImageFill(fillBackground, removeBackground);
            UpdateAndRemoveLogic.checkPatternFill(fillBackground, removeBackground);
        } else if (uiProps?.style?.border) {
            const borderInfo = updateProps.style.border;
            const removeBorder = {};
            removeProps = { style: { border: removeBorder } };
            updateProps = copy(uiProps, true);
            UpdateAndRemoveLogic.checkBorderGradFill(borderInfo, removeBorder, modelProps);
            UpdateAndRemoveLogic.checkNoFillGradFill(borderInfo, removeBorder, modelProps);
            UpdateAndRemoveLogic.clearBorderFillNull(updateProps.style.border);
            if (Object.keys(removeProps.style.border).length === 0) {
                removeProps = null;
            }
        }

        if (has(modelProps, 'style.border.sketchStyle') && uiProps.style?.border?.sketchStyle === null) {
            set(removeProps, 'style.border.sketchStyle', null);
        }
        if (has(modelProps, 'style.pathList') && uiProps.style?.customGeom === null) {
            set(removeProps, 'style.customGeom', null);
        }
        return { update: updateProps, remove: removeProps };
    };

    /**
     * updateProps中若gradFill和color同时存在，sdk内部因为互斥逻辑(gradual>color)会只认优先级高的，将优先级低的清掉，即使优先级高的是null
     * 所以设置一个值的时候将另一个值delete掉，防止互斥掉另一个值
     * @param border
     * @private
     */
    private static clearBorderFillNull(border) {
        ['gradFill', 'color'].forEach((p) => {
            if (border[p] === null) {
                delete border[p];
            }
        });
    }

    /**
     * 纯色填充
     * @param {*} fillBackground 将要填充的背景色
     * @param {*} removeBackground 需要移除的背景色
     */
    private static checkSolidFill(fillBackground, removeBackground) {
        if (!has(fillBackground, 'solidFill')) {
            removeBackground.solidFill = null;
        } else {
            removeBackground.noFill = null;
        }
    }

    /**
     * 渐变填充
     * @param {*} fillBackground 将要填充的渐变色
     * @param {*} removeBackground 需要移除的背景色
     * @param {*} modelProps
     */
    private static checkGradFill(fillBackground, removeBackground, modelProps) {
        if (!has(fillBackground, 'gradFill')) {
            removeBackground.gradFill = null;
        } else {
            if (get(fillBackground, 'gradFill.gsList')) {
                if (get(modelProps, 'style.fillBackground.gradFill.gsList')) {
                    const { gsList } = fillBackground.gradFill;
                    const sdkGsList = modelProps.style.fillBackground.gradFill.gsList;
                    const sdkLen = safeLength(sdkGsList.length);
                    const uiLen = safeLength(gsList.length);
                    if (sdkLen > uiLen) {
                        const removeArray: any[] = [];
                        for (let i = 0; i < sdkLen; i++) {
                            if (i < uiLen) {
                                removeArray.push(undefined);
                            } else {
                                removeArray.push({});
                            }
                        }
                        set(removeBackground, 'gradFill.gsList', removeArray);
                    }
                }
            }
            if (!get(fillBackground, 'gradFill.lin')) {
                set(removeBackground, 'gradFill.lin', null);
            }
            if (!get(fillBackground, 'gradFill.pathGradient')) {
                set(removeBackground, 'gradFill.pathGradient', null);
            }
            removeBackground.noFill = null;
        }
    }

    /**
     * 图片填充
     * @param {*} fillBackground 将要填充的
     * @param {*} removeBackground 原有的填充
     */
    private static checkImageFill(fillBackground, removeBackground) {
        if (!has(fillBackground, 'image')) {
            removeBackground.image = null;
        } else {
            const imageAlpha = get(fillBackground, 'image.alpha');
            // 避免出现imageAlpha为0被删掉的情况
            if (typeof imageAlpha !== 'number' && !imageAlpha) {
                set(removeBackground, 'image.alpha', null); // 转换为sdk数据格式
            }

            // stretch和tile互斥
            if (get(fillBackground, 'image.stretch')) {
                set(removeBackground, 'image.tile', null);
            } else if (get(fillBackground, 'image.tile')) {
                set(removeBackground, 'image.stretch', null);
            }

            if (!has(fillBackground, 'image.stretch')) {
                set(removeBackground, 'image.stretch', null);
            }
            if (!has(fillBackground, 'image.tile')) {
                set(removeBackground, 'image.tile', null);
            }

            removeBackground.noFill = null;
        }
    }

    /**
     * 图案填充
     * @param fillBackground
     * @param removeBackground
     */
    private static checkPatternFill(fillBackground, removeBackground) {
        if (!fillBackground?.pattFill) {
            removeBackground.pattFill = null;
        } else {
            removeBackground.noFill = null;
        }
    }

    /**
     * 无边框
     * @param {*} borderInfo 将要填充的渐变色
     * @param {*} removeBorder 需要移除的背景色
     */
    private static checkNoFillGradFill(borderInfo, removeBorder, modelProps) {
        if (borderInfo.width === 0) {
            Object.keys(modelProps?.style?.border || {}).forEach((p) => {
                // 设置无边框和调整实线边框的线宽为0，都会出现borderInfo.width === 0情况
                // 不能无脑的把所有属性都清掉，因为没有color时显示部分会判定为无边框状态,从而出现调整边框为0时直接变成无边框类型
                // 所以只有当设置无边框时（width ===0,color===null），才清掉color。其他情况保留color
                if (p !== 'color' && p !== 'colorValue' && p !== 'gradFill') {
                    removeBorder[p] = null;
                }
            });
            if (borderInfo.color === null) {
                removeBorder.color = null;
                removeBorder.colorValue = null;
            }
            if (borderInfo.gradFill === null) {
                removeBorder.gradFill = null;
            }
            delete removeBorder.width;
        }
    }
    /**
     * 边框渐变填充
     * @param {*} borderInfo 将要填充的渐变色
     * @param {*} removeBorder 需要移除的背景色
     */
    private static checkBorderGradFill(borderInfo, removeBorder, modelProps) {
        if (borderInfo.gradFill) {
            if (borderInfo?.gradFill?.gsList) {
                if (modelProps?.style?.border?.gradFill?.gsList) {
                    const { gsList } = borderInfo.gradFill;
                    const sdkGsList = modelProps.style.border.gradFill.gsList;
                    const sdkLen = safeLength(sdkGsList.length);
                    const uiLen = safeLength(gsList.length);
                    if (sdkLen > uiLen) {
                        const removeArray: any[] = [];
                        for (let i = 0; i < sdkLen; i++) {
                            if (i < uiLen) {
                                removeArray.push(undefined);
                            } else {
                                removeArray.push({});
                            }
                        }
                        set(removeBorder, 'gradFill.gsList', removeArray);
                    }
                }
            }
            if (!borderInfo?.gradFill?.lin) {
                set(removeBorder, 'gradFill.lin', null);
            }
            if (!borderInfo?.gradFill?.pathGradient) {
                set(removeBorder, 'gradFill.pathGradient', null);
            }
        } else if (borderInfo.gradFill === null) {
            removeBorder.gradFill = null;
        }
    }
}

function safeLength(length) {
    return length || 0;
}
